import React, { useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  beginHistoryInteraction,
  endHistoryInteraction,
} from '../domain/document-lifecycle';
import {
  isFineResizeStep,
  resolveTextBlockWidthCommit,
  resolveTextWidthResizePointerScale,
  snapBlockWidthPx,
  textBlockStartWidthPx,
  textWidthGuideLineLeft,
} from '../domain/block-width';
import type { TextBlock } from '../domain/types';
import { ZERO_PADDING } from '../domain/create-shared';
import { readEmailBuilderMobileCanvasLayoutScale } from '../shell/canvas/emailBuilderMobileCanvasLayoutScale';
import { WidthGuide } from './WidthGuide';

type TextResizeHandlesProps = {
  block: TextBlock;
  contentMaxWidth: number;
  onPatch: (patch: Partial<TextBlock>) => void;
  onPreviewWidthChange?: (widthPx: number | null) => void;
  /** When handles sit inside the padded content box (auto-width editor path). */
  inContentBox?: boolean;
};

type DragState = {
  startX: number;
  startWidthPx: number;
  currentWidthPx: number;
  layoutScale: number;
};

export function TextResizeHandles({
  block,
  contentMaxWidth,
  onPatch,
  onPreviewWidthChange,
  inContentBox = false,
}: TextResizeHandlesProps) {
  const { t } = useTranslation();
  const dragging = useRef(false);
  const dragState = useRef<DragState | null>(null);
  const [dragWidthPx, setDragWidthPx] = useState<number | null>(null);

  function finishDrag(event: PointerEvent<HTMLDivElement>) {
    if (!dragging.current) {
      return;
    }
    const state = dragState.current;
    dragging.current = false;
    dragState.current = null;
    const committedWidth = state?.currentWidthPx ?? dragWidthPx;
    setDragWidthPx(null);
    onPreviewWidthChange?.(null);
    if (committedWidth != null) {
      const widthPx = resolveTextBlockWidthCommit(
        committedWidth,
        contentMaxWidth,
      );
      if (widthPx !== block.widthPx) {
        onPatch({ widthPx });
      }
    }
    endHistoryInteraction();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.currentTarget.dataset.dragging = 'false';
  }

  function startDrag(event: PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragging.current = true;
    const startWidthPx = textBlockStartWidthPx(block, contentMaxWidth);
    dragState.current = {
      startX: event.clientX,
      startWidthPx,
      currentWidthPx: startWidthPx,
      layoutScale: readEmailBuilderMobileCanvasLayoutScale(event.currentTarget),
    };
    setDragWidthPx(startWidthPx);
    onPreviewWidthChange?.(startWidthPx);
    beginHistoryInteraction();
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.dataset.dragging = 'true';
  }

  function applyResize(event: PointerEvent<HTMLDivElement>) {
    const state = dragState.current;
    if (!dragging.current || !state) {
      return;
    }

    const fine = isFineResizeStep(event);
    const fromCenter = event.altKey;
    const visualDeltaX = event.clientX - state.startX;
    const layoutScale = state.layoutScale > 0 ? state.layoutScale : 1;
    const pointerScale = resolveTextWidthResizePointerScale(
      block.align,
      fromCenter,
    );
    const deltaX = (visualDeltaX / layoutScale) * pointerScale;
    const widthPx = snapBlockWidthPx(
      state.startWidthPx + deltaX,
      contentMaxWidth,
      fine,
    );

    dragState.current = { ...state, currentWidthPx: widthPx };
    setDragWidthPx(widthPx);
    onPreviewWidthChange?.(widthPx);
  }

  const guideWidth = dragWidthPx ?? block.widthPx;
  const activeWidth =
    guideWidth ?? textBlockStartWidthPx(block, contentMaxWidth);
  const lineLeft = inContentBox
    ? textWidthGuideLineLeft(
        block.align,
        ZERO_PADDING,
        activeWidth,
        contentMaxWidth,
      )
    : textWidthGuideLineLeft(
        block.align,
        block.padding,
        activeWidth,
        contentMaxWidth,
      );
  const showWidthGuide = guideWidth != null;
  const readoutWidth = `${activeWidth}px`;

  return (
    <div className={"email-builder-image-resize-overlay"} data-editor-chrome>
      {showWidthGuide && lineLeft != null ? (
        <WidthGuide lineLeft={lineLeft} dragging={dragWidthPx != null} />
      ) : null}
      <div
        className={"email-builder-image-resize-handle"}
        data-axis="width"
        data-at-line="true"
        style={{ left: lineLeft, top: '50%' }}
        tabIndex={-1}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Resize text width')}
        onPointerDown={startDrag}
        onPointerMove={applyResize}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}>
        <span className={"email-builder-image-resize-dot"} />
        <span className={"email-builder-image-resize-readout"}>{readoutWidth}</span>
      </div>
    </div>
  );
}
